// 付箋の背景色に応じて、本文・署名の文字色を「読める色」へ切り替えるためのユーティリティ。
//
// 付箋の文字色は従来「黒（#1a1a2e）」固定だったが、利用者が濃い背景色を指定すると本文が
// 背景に埋もれて読めなくなる。そこで背景色と文字色のコントラスト比（WCAG 相対輝度ベース）を
// 計算し、暗い文字より明るい文字（白）の方がコントラストが高い＝読みやすい背景では、文字色を
// 白へ反転させる。これにより付箋がどんな色でも本文が読めるようにする。
//
// 画面表示（sticky-object.tsx）・PDF 変換（canvas-pdf-export.ts）の双方で同じ判定を共有し、
// 印刷・PDF でも見た目どおり（背景に応じた文字色）になるようにする。

import { isValidHexColor } from "./pen-preference";
import { contrastRatioOfLuminance, relativeLuminanceOfHex } from "./color-contrast";

/** 明るい背景で使う本文色（従来の付箋文字色。実質黒）。 */
export const STICKY_TEXT_COLOR_DARK = "#1a1a2e";
/** 暗い背景で使う本文色（白へ反転）。 */
export const STICKY_TEXT_COLOR_LIGHT = "#ffffff";
/** 明るい背景で使う署名色（従来どおりの薄い黒）。 */
export const STICKY_SIGNATURE_COLOR_DARK = "rgba(0,0,0,0.45)";
/** 暗い背景で使う署名色（薄い白へ反転）。 */
export const STICKY_SIGNATURE_COLOR_LIGHT = "rgba(255,255,255,0.7)";

/** 付箋色未指定時の既定背景色（sticky-object.tsx / canvas-pdf-export.ts と同じ値）。 */
const STICKY_DEFAULT_BACKGROUND = "#fff5b3";

/** 付箋の文字色パレット（背景色から解決した本文色・署名色の組）。 */
export interface StickyTextPalette {
  /** 本文の文字色。 */
  readonly body: string;
  /** 左下署名の文字色。 */
  readonly signature: string;
  /** 明るい文字（白系）へ反転したか（暗い背景と判定したか）。 */
  readonly isLightText: boolean;
}

/**
 * 付箋の背景色から、読みやすい本文色・署名色のパレットを解決する。
 *
 * 判定規則:
 * - 背景色に対して「暗い文字（{@link STICKY_TEXT_COLOR_DARK}）」と
 *   「明るい文字（{@link STICKY_TEXT_COLOR_LIGHT}）」のコントラスト比をそれぞれ計算し、
 *   明るい文字の方が高コントラスト（＝読みやすい）場合のみ白へ反転する。
 * - 背景色が hex として解釈できない場合（未指定・named color・rgb() など）は、既定の淡い付箋色を
 *   前提として暗い文字を採用する（安全側。従来挙動を維持）。
 */
export function resolveStickyTextPalette(background: string | undefined): StickyTextPalette {
  const source = isValidHexColor(background) ? background : STICKY_DEFAULT_BACKGROUND;
  const backgroundLuminance = relativeLuminanceOfHex(source);
  const darkTextContrast = contrastRatioOfLuminance(
    backgroundLuminance,
    relativeLuminanceOfHex(STICKY_TEXT_COLOR_DARK)
  );
  const lightTextContrast = contrastRatioOfLuminance(
    backgroundLuminance,
    relativeLuminanceOfHex(STICKY_TEXT_COLOR_LIGHT)
  );
  const isLightText = lightTextContrast > darkTextContrast;
  return {
    body: isLightText ? STICKY_TEXT_COLOR_LIGHT : STICKY_TEXT_COLOR_DARK,
    signature: isLightText ? STICKY_SIGNATURE_COLOR_LIGHT : STICKY_SIGNATURE_COLOR_DARK,
    isLightText,
  };
}
